// ADR 5.9.26u §3 — the compact chrome: a bar across the foot of the window.
//
// Rendered when `navChrome(width)` is `bottom_bar`, which is the table's
// decision and not this file's. The same destinations as the rail and the
// sidebar (`destinations.ts`), drawn where a thumb can reach them.
//
// **Labelled since ADR 15.9.26ah §1** — Material 3's navigation bar: the icon
// in a 64×32 active pill, a 12px label under it, `pt-3 … pb-4` composing to
// the bar's 80, and the safe-area inset padded *inside* the bar below the
// labels. ADR 10.9.26h §4 had made this bar icon-only from arithmetic (five
// slots at 320 CSS px leave 64px each, and `tab_flashcards` is ~78px in `de`
// and `fi`); 15.9.26ah §2 re-ran it with the answer Material's own bar uses —
// a single line, **truncated with an ellipsis**, the full name on `title` and
// in the accessibility tree — and the product owner took that trade. The link
// carries no horizontal padding on purpose: the `<li>` is `flex-1`, so the
// slot's whole width is the label's. The rail and the sidebar keep drawing the
// same list; only this one ever withheld the name.

import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { FOCUS_RING } from '../../ui/Button'
import { Icon } from '../../ui/icons'
import { SoonNavItem } from './SoonNavItem'
import type { Destination } from './destinations'

export function BottomNav({ items }: { items: Destination[] }) {
  return (
    <nav
      data-testid="bottom-nav"
      className="sticky bottom-0 border-t border-boundary bg-surface pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="flex">
        {items.map((item) => (
          // `min-w-0`: without it a flex item's floor is its content's
          // min-content width, so `Karteikarten` would widen its slot to 69px
          // and push the bar 5px past the viewport rather than ellipsise
          // (measured at 320, *As built*).
          <li key={item.path} className="min-w-0 flex-1">
            <BottomNavItem item={item} />
          </li>
        ))}
      </ul>
    </nav>
  )
}

const BAR_ITEM = 'group flex flex-col items-center gap-1 pt-3 pb-4 text-label-md'
const BAR_LABEL = 'max-w-full truncate'

/**
 * ADR 30.9.26s §3 — one bar item: a link, or a reserved destination drawn
 * disabled (its badge under the label, as the rail's is).
 */
function BottomNavItem({ item }: { item: Destination }) {
  const { t } = useTranslation()
  if (item.soon) return <SoonNavItem item={item} className={BAR_ITEM} labelClassName={BAR_LABEL} />
  return (
    <NavLink
      to={item.path}
      end={item.end}
      // `title` is what a pointer user reads when the label is ellipsised.
      title={t(item.labelKey)}
      className={({ isActive }) =>
        `${BAR_ITEM} ${FOCUS_RING} motion-safe:transition-colors duration-short ${
          isActive ? 'font-medium text-ink' : 'text-ink-muted'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex h-8 w-16 items-center justify-center rounded-full ${
              isActive ? 'bg-secondary-soft text-on-secondary-soft' : 'group-hover:bg-surface-sunken'
            }`}
          >
            <Icon name={item.icon} />
          </span>
          <span className={BAR_LABEL}>{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  )
}
